import { useMemo } from "react";

interface VehicleMapping {
  svm_id: string;
  service_cost: number;
}

interface Service {
  vehicle_mappings: VehicleMapping[];
}

interface VendorSettings {
  platform_fees: number;
  booking_charges?: number; // For other service bookings
}

interface VendorKycDetails {
  gst_no?: string;
}

interface Vendor {
  vp_id: string;
  vendor_kyc_details: VendorKycDetails;
  vendor_settings: VendorSettings;
}

interface UsePriceCalculationProps {
  serviceMappingId: string | undefined;  // Make serviceMappingId optional
  vendorId: string;
  availableServices: Service[];
  availableVendors: Vendor[];
  publicAdminSettings: {
    rzp_platform_fees: number;
    rzp_route_transfer_fees: number;
    gst_value: number;
  };
  isOtherService?: boolean;
}

/**
 * Calculates the final customer price. This single function handles both
 * GST and non-GST registered vendors.
 */
function calculatePrice(
  platformFee: number,
  vendorCost: number,
  gstDecimal: number,
  pgDecimal: number,
  routeDecimal: number,
  isVendorGst: boolean
): number {
  const pgRateWithGst = pgDecimal * (1 + gstDecimal);
  const routeRateWithGst = routeDecimal * (1 + gstDecimal);
  const adminAmount = platformFee * (1 + gstDecimal);
  const vendorTransferAmount = isVendorGst
    ? vendorCost * (1 + gstDecimal)
    : vendorCost;
  const totalSettlement = adminAmount + (vendorTransferAmount * (1 + routeRateWithGst));
  return totalSettlement / (1 - pgRateWithGst);
}

export const usePriceCalculation = ({
  serviceMappingId,
  vendorId,
  availableServices,
  availableVendors,
  publicAdminSettings,
  isOtherService = false
}: UsePriceCalculationProps) => {
  return useMemo(() => {
    const selectedVendor = availableVendors.find(
      vendor => vendor.vp_id === vendorId
    );

    if (!selectedVendor) {
      return { finalTotal: 0, vendorPayout: 0, hasVendorGst: false, platformFeesWithGst: 0, vendorBookingCharges: 0 };
    }

    // Extract necessary values
    const platformFees = selectedVendor?.vendor_settings.platform_fees || 0;
    const vendorBookingCharges = selectedVendor?.vendor_settings.booking_charges || 0;
    const hasVendorGst = !!selectedVendor?.vendor_kyc_details.gst_no;
    const gstRate = Number(publicAdminSettings?.gst_value || 18) / 100;
    const pgRate = Number(publicAdminSettings?.rzp_platform_fees) / 100;
    const routeRate = Number(publicAdminSettings?.rzp_route_transfer_fees || 0.25) / 100;

    let serviceCost = 0;
    if (isOtherService) {
      serviceCost = vendorBookingCharges; // <-- only vendor booking charges
    } else if (serviceMappingId) {
      const selectedService = availableServices.find(service =>
        service.vehicle_mappings.some(mapping => mapping.svm_id === serviceMappingId)
      );

      serviceCost = selectedService?.vehicle_mappings.find(
        mapping => mapping.svm_id === serviceMappingId
      )?.service_cost ?? 0;
    }

    // 1. Calculate the final total to charge the customer
    const totalAmount = calculatePrice(
      platformFees,
      serviceCost,
      gstRate,
      pgRate,
      routeRate,
      hasVendorGst
    );

    // 2. Calculate the vendor's payout amount
    const vendorPayoutAmount = hasVendorGst
      ? serviceCost * (1 + gstRate)
      : serviceCost;

    // 3. Calculate platform fees inclusive of GST
    const platformFeesWithGst = platformFees * (1 + gstRate);

    return {
      finalTotal: Number(totalAmount.toFixed(2)),
      vendorPayout: Number(vendorPayoutAmount.toFixed(2)),
      hasVendorGst,
      platformFeesWithGst: Number(platformFeesWithGst.toFixed(2)),
      vendorBookingCharges
    };

  }, [serviceMappingId, vendorId, availableServices, availableVendors, publicAdminSettings]);
};